import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { CasesRepository } from '../../../infrastructure/repositories/cases.repository';
import { UploadAttachmentCommand } from './upload-attachment.command';
import { UploadAttachmentResult } from './upload-attachment.result';

/**
 * Upload attachment use case.
 * Pure business logic - uploads file to S3 and creates attachment record via 3-step flow.
 * Supports optional progress tracking for future UI improvements.
 *
 * No state management or error mapping - component handles all presentation concerns.
 * Follows Clean Architecture: use case orchestrates business logic only.
 */
@Injectable({ providedIn: 'root' })
export class UploadAttachmentUseCase {
  private readonly _repository = inject(CasesRepository);

  /**
   * Executes file upload to S3 and creates attachment record via 3-step flow:
   * 1. Get pre-signed upload URL from backend
   * 2. Upload file directly to S3 (bypasses Angular HttpClient)
   * 3. Confirm upload completion with backend
   *
   * Pure use case - no state management or side effects. Calling component
   * is responsible for managing loading states, updating data lists, and
   * presenting results/errors to the user.
   *
   * @param command - Upload command containing caseId and file
   * @param onProgress - Optional callback for progress updates (0-100 percent)
   * @returns Observable that emits upload result with created attachment entity
   *
   * @throws AttachmentError with appropriate type:
   *   - FILE_TOO_LARGE: file exceeds 100MB limit
   *   - CASE_CLOSED: case is closed or resolved
   *   - UPLOAD_EXPIRED: pre-signed URL expired (>15 minutes)
   *   - NETWORK_ERROR: network connectivity issues
   *   - S3_UPLOAD_FAILED: S3 storage upload failed
   *   - UPLOAD_ABORTED: upload was cancelled
   *   - UNKNOWN: unexpected errors
   *
   * Observable behavior:
   *   - Emits once: { attachment: CaseAttachmentEntity } when upload completes
   *   - Completes: after successful 3-step flow
   *   - Errors: if any step fails (throws AttachmentError)
   *   - Progress: calls onProgress callback during S3 upload (if provided)
   *
   * Component responsibilities:
   *   - Set loading state before calling execute()
   *   - Add attachment to state on success
   *   - Clear loading state in finalize()
   *   - Map errors to user messages via AttachmentErrorMapperService
   *   - Display toast notifications
   */
  public execute(
    command: UploadAttachmentCommand,
    onProgress?: (progress: number) => void
  ): Observable<UploadAttachmentResult> {
    return this._repository.uploadAttachment(
      command.caseId,
      command.file,
      onProgress
    ).pipe(
      map((attachment) => ({ attachment }))
    );
  }
}
